import { db } from "./db.js";
import { ref } from "vue";
import { getMemberCaching, getCachedMemberRole } from "./teams.js";

// ==================== CREATE ====================

function addTrademark(firmId, data) {
  const ref = db.collection(`firms/${firmId}/additions/tm-addition/trademarks`).doc(data.serialNumber);
  return ref.set(data);
}

// Manually created matters (interim for foreign/unmonitored marks) use a
// caller-provided application/serial number when given, else a MANUAL- id —
// mirrors createManualPatent. firestore.rules require manuallyCreated: true
// and manager/admin.
function createManualTrademark(firmId, data) {
  const collectionRef = db.collection(`firms/${firmId}/additions/tm-addition/trademarks`);
  const id = (data.serialNumber && data.serialNumber.trim()) || `MANUAL-${collectionRef.doc().id}`;
  return collectionRef.doc(id).set({
    alive: true,
    internationalClass: [],
    assigned: [],
    manuallyCreated: true,
    ...data,
    serialNumber: id,
  });
}

// ==================== READ ====================

async function getTrademarks(firmId) {
  const trademarks = ref([]);
  await db
    .collection(`firms/${firmId}/additions/tm-addition/trademarks`)
    .get()
    .then((querySnapshot) => {
        trademarks.value = querySnapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
  return trademarks;
}

function streamTrademarks(firmId, onSnap) {
  return db
    .collection(`firms/${firmId}/additions/tm-addition/trademarks`)
    .onSnapshot(onSnap, (error) => { console.log(error); });
}

// Role-aware variant (mirrors getPatentsOfUser): admins/managers stream the
// whole collection; members and clients stream only marks whose assigned[]
// contains their uid. Uses the cached role to attach instantly on repeat
// visits, re-checking membership in the background and re-attaching if the
// role changed (a stale over-privileged role is denied by the rules, so this
// fails safe). Resolves to the unsubscribe function.
async function streamTrademarksOfUser(firmId, userId, onSnap) {
  const collectionRef = db.collection(`firms/${firmId}/additions/tm-addition/trademarks`);
  const queryFor = (role) =>
    role === "admin" || role === "manager"
      ? collectionRef
      : collectionRef.where("assigned", "array-contains", userId);
  const onError = (error) => { console.log(error); };

  const cachedRole = getCachedMemberRole(firmId, userId);
  if (cachedRole) {
    let unsubscribe = queryFor(cachedRole).onSnapshot(onSnap, onError);
    getMemberCaching(firmId, userId).then((member) => {
      const freshRole = (member && member.role) || "member";
      if (freshRole !== cachedRole) {
        unsubscribe();
        unsubscribe = queryFor(freshRole).onSnapshot(onSnap, onError);
      }
    });
    return () => unsubscribe();
  }

  const member = await getMemberCaching(firmId, userId);
  return queryFor((member && member.role) || "member").onSnapshot(onSnap, onError);
}

async function getTrademark(firmId, serialNumber) {
  return db.doc(`firms/${firmId}/additions/tm-addition/trademarks/${serialNumber}`)
    .get()
    .then((trademark) => {
      return trademark.exists ? trademark.data() : null;
    });
}

// ==================== UPDATE ====================

async function updateTrademark(firmId, serialNumber, data, transaction = null) {
  const ref = db.doc(`firms/${firmId}/additions/tm-addition/trademarks/${serialNumber}`);
  if (transaction) {
    transaction.update(ref, data);
  } else {
    await ref.update(data);
  }
}

async function getTrademarkDocuments(firmId, serialNumber) {
  const snapshot = await db
    .collection(`firms/${firmId}/additions/tm-addition/trademarks/${serialNumber}/documents`)
    .get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

// Single document record, shaped like getLatestOfficeActionDoc's return so the
// OA workspace can load a specific document instead of the latest OA
async function getTrademarkDocument(firmId, serialNumber, docId) {
  const snap = await db
    .doc(`firms/${firmId}/additions/tm-addition/trademarks/${serialNumber}/documents/${docId}`)
    .get();
  if (!snap.exists) return null;
  const data = snap.data();
  const urls = data.urls || [];
  const oaUrl = urls.find(u => u.toLowerCase().includes("office-action"))
    || urls.find(u => u.toLowerCase().endsWith(".pdf"))
    || null;
  return {
    id: snap.id,
    url: oaUrl,
    originalFileName: data.originalFileName || "",
    ...data,
  };
}

// Pick the office action (NFIN_/FREF_) a docketing task refers to: tasks
// carry no document id, so use the OA whose mail date is the latest on or
// before the task's creation — the OA that was current when the deadline was
// docketed (a later, unrelated OA must not win). Falls back to the latest OA.
async function getOfficeActionDocForTask(firmId, serialNumber, taskCreatedAt) {
  const snapshot = await db
    .collection(`firms/${firmId}/additions/tm-addition/trademarks/${serialNumber}/documents`)
    .get();
  const oas = snapshot.docs
    .filter(d => d.id.startsWith("NFIN_") || d.id.startsWith("FREF_"))
    .map(d => ({ id: d.id, ...d.data() }));
  if (!oas.length) return null;
  const dateOf = (d) => (d.mailRoomDate || d.scanDateTime || "").substring(0, 10);
  oas.sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
  if (taskCreatedAt) {
    const asOfDate = taskCreatedAt.toDate ? taskCreatedAt.toDate() : new Date(taskCreatedAt);
    const asOf = asOfDate.toISOString().substring(0, 10);
    const eligible = oas.filter(d => dateOf(d) <= asOf);
    if (eligible.length) return eligible[eligible.length - 1];
  }
  return oas[oas.length - 1];
}

async function getLatestOfficeActionDoc(firmId, serialNumber) {  const snapshot = await db.collection(`firms/${firmId}/additions/tm-addition/trademarks/${serialNumber}/documents`).get();
  if (snapshot.empty) return null;

  const candidates = snapshot.docs
    .filter(d => d.id.startsWith("NFIN_") || d.id.startsWith("FREF_"))
    .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true }));

  if (!candidates.length) return null;

  const doc = candidates[0];
  const data = doc.data();
  const urls = data.urls || [];
  const oaUrl = urls.find(u => u.toLowerCase().includes("office-action")) || null;
  return {
    id: doc.id,
    url: oaUrl,
    originalFileName: data.originalFileName || "",
    ...data,
  };
}

export {
  addTrademark,
  createManualTrademark,
  getTrademarks,
  getTrademark,
  getTrademarkDocument,
  getTrademarkDocuments,
  getOfficeActionDocForTask,
  streamTrademarks,
  streamTrademarksOfUser,
  updateTrademark,
  getLatestOfficeActionDoc,
};
